import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';

import { AuthService } from './auth.service';

// Stato condiviso per gestire refresh multipli simultanei
let isRefreshing = false;
const refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<
  string | null
>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  // Non intercettare le richieste di refresh per evitare di sovrascrivere l'header
  if (req.url.includes('/auth/refresh')) {
    return next(req);
  }

  // Ottieni il token corrente usando il getter del tuo service
  const token = authService.currentAccessToken;

  // Controlla se il token è scaduto o sta per scadere
  if (token && authService.isTokenExpired(token)) {
    // Se il token è scaduto, prova il refresh prima di fare la richiesta
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken && !authService.isTokenExpired(refreshToken)) {
      // Se il refresh token è valido, gestisci il refresh
      return handleTokenRefresh(req, next, authService);
    } else {
      // Se anche il refresh token è scaduto, vai al logout locale
      authService.logoutLocal();
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 401,
            statusText: 'Token scaduto',
          })
      );
    }
  }

  // Aggiungi il token all'header se disponibile
  let authReq = req;
  if (token) {
    authReq = addTokenToRequest(req, token);
  }

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Se ricevi 401, prova il refresh
      if (error.status === 401 && token) {
        return handleTokenRefresh(req, next, authService);
      }
      return throwError(() => error);
    })
  );
};

/**
 * Gestisce il refresh del token in modo thread-safe
 */
function handleTokenRefresh(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService
): Observable<HttpEvent<unknown>> {
  const refreshToken = localStorage.getItem('refreshToken');

  if (!refreshToken) {
    authService.logoutLocal();
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 401,
          statusText: 'Nessun refresh token disponibile',
        })
    );
  }

  // Se è già in corso un refresh, aspetta il risultato
  if (isRefreshing) {
    return refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(jwt => {
        return next(addTokenToRequest(req, jwt));
      })
    );
  }

  // Inizia un nuovo processo di refresh
  isRefreshing = true;
  refreshTokenSubject.next(null);

  return authService.refreshToken().pipe(
    switchMap(tokenResponse => {
      // I token vengono già salvati automaticamente nel tuo service tramite tap()

      // Notifica tutti i subscriber in attesa
      const newToken = tokenResponse.accessToken;
      refreshTokenSubject.next(newToken);

      // Riprova la richiesta originale con il nuovo token
      return next(addTokenToRequest(req, newToken));
    }),
    catchError(refreshError => {
      // Se il refresh fallisce, esegui il logout locale
      console.error('Refresh token fallito:', refreshError);
      authService.logoutLocal();

      return throwError(
        () =>
          new HttpErrorResponse({
            status: 401,
            statusText: 'Sessione scaduta',
            error: refreshError,
          })
      );
    }),
    finalize(() => {
      // Reset dello stato di refresh
      isRefreshing = false;
    })
  );
}

/**
 * Aggiunge il token all'header della richiesta
 */
function addTokenToRequest(request: HttpRequest<unknown>, token: string) {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}
